import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowRight } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { TextField } from '@/components/ui/Field';
import { InlineError } from '@/components/ui/EmptyState';
import { useLogin } from '@/lib/mutations';
import { toFieldErrors, toMessage } from '@/lib/api';
import { useAuthStore } from '@/store/auth';

const schema = z.object({
  email: z.string().min(1, 'Введите адрес почты.').email('Некорректный адрес почты.'),
  password: z.string().min(1, 'Введите пароль.'),
});

type FormValues = z.infer<typeof schema>;

interface LocationState {
  from?: { pathname?: string; search?: string };
}

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const signIn = useAuthStore((state) => state.signIn);
  const login = useLogin();
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  });

  // Where the trader was heading before the auth gate intercepted them.
  const state = location.state as LocationState | null;
  const intended = state?.from?.pathname;
  const destination =
    intended && intended !== '/login' && intended !== '/signup'
      ? `${intended}${state?.from?.search ?? ''}`
      : '/dashboard';

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);
    try {
      const result = await login.mutateAsync(values);
      signIn(result.tokens.accessToken, result.tokens.expiresIn, result.trader);
      navigate(destination, { replace: true });
    } catch (error) {
      const fieldErrors = toFieldErrors(error);
      let matched = false;
      for (const [field, message] of Object.entries(fieldErrors)) {
        if (field === 'email' || field === 'password') {
          setError(field, { message });
          matched = true;
        }
      }
      if (!matched) setServerError(toMessage(error, 'Не удалось выполнить вход.'));
    }
  });

  return (
    <div>
      <p className="eyebrow">Вход</p>
      <h1 className="mt-2 text-xl font-semibold leading-tight tracking-tight text-fg">
        Кабинет трейдера
      </h1>
      <p className="mt-2 text-sm text-fg-secondary">
        Войдите с почтой, на которую регистрировались. Сессии истекают сами — на общих компьютерах
        выходите вручную.
      </p>

      {serverError ? <InlineError message={serverError} className="mt-6" /> : null}

      <form onSubmit={onSubmit} noValidate className="mt-6 space-y-4">
        <TextField
          label="Почта"
          type="email"
          inputMode="email"
          enterKeyHint="next"
          autoComplete="username"
          autoCapitalize="none"
          autoCorrect="off"
          autoFocus
          spellCheck={false}
          placeholder="you@example.com"
          error={errors.email?.message}
          {...register('email')}
        />

        <TextField
          label="Пароль"
          type="password"
          enterKeyHint="go"
          autoComplete="current-password"
          error={errors.password?.message}
          {...register('password')}
        />

        <Button
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          isLoading={isSubmitting}
          className="mt-6"
        >
          {isSubmitting ? 'Вхожу…' : 'Войти'}
        </Button>
      </form>

      <p className="mt-8 border-t border-line pt-5 text-sm text-fg-secondary">
        Ещё нет аккаунта?{' '}
        <Link
          to="/signup"
          className="focus-ring tap-target inline-flex items-center gap-1 rounded-sm font-medium text-fg-accent underline underline-offset-2"
        >
          Зарегистрироваться
          <ArrowRight size={13} strokeWidth={1.5} aria-hidden="true" />
        </Link>
      </p>
    </div>
  );
}
